import type { RowData } from "@tanstack/react-table";
import type { DataGridColumnInstance } from "../columns/types";
import { columnWidthVar } from "../layout/columnVars";

interface MeasureColumnInput<TData extends RowData> {
    column: DataGridColumnInstance<TData>;
    root: HTMLElement | null;
}

/**
 * The widest rendered header or cell of a column, measured with the width var
 * switched to `max-content` and then restored. Only mounted rows count; `null`
 * when none are found.
 */
export const measureColumnContent = <TData extends RowData>({
    column,
    root,
}: MeasureColumnInput<TData>): number | null => {
    if (!root) {
        return null;
    }
    const id = CSS.escape(column.id);
    const elements = root.querySelectorAll<HTMLElement>(
        `[role="gridcell"][data-column-id="${id}"], [role="columnheader"][data-column-id="${id}"]`,
    );
    if (elements.length === 0) {
        return null;
    }
    const widthVar = columnWidthVar(column.id);
    const previous = root.style.getPropertyValue(widthVar);
    root.style.setProperty(widthVar, "max-content");
    let widest = 0;
    for (const element of elements) {
        widest = Math.max(
            widest,
            Math.ceil(element.getBoundingClientRect().width),
        );
    }
    root.style.setProperty(widthVar, previous);
    return widest;
};
